// frontend/src/modules/invoicing/components/PayLinkButton.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { paymentsApi } from '../../payments/api';
import { ApiError } from '../../../core/api-client';

interface Props {
  invoiceId: string;
  disabled?: boolean;
}

export function PayLinkButton({ invoiceId, disabled }: Props) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [url, setUrl] = React.useState<string | null>(null);

  async function generate() {
    setBusy(true);
    setUrl(null);
    try {
      const res = await paymentsApi.payLink(invoiceId);
      setUrl(res.url);
      setOpen(true);
    } catch (err: any) {
      if (err instanceof ApiError && err.code === 'no_gateway') {
        toast.error('Stripe not configured', {
          description: 'Open the Payments page and add your Stripe keys before generating pay links.',
        });
      } else {
        toast.error('Could not generate link', { description: err.message ?? String(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — copy manually');
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" disabled={busy || disabled} onClick={generate}>
        {busy ? 'Generating…' : 'Get pay link'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share this Stripe checkout link</DialogTitle>
            <DialogDescription>
              Send this URL to your client. When they pay, the invoice will auto-mark as paid and a receipt is emailed.
            </DialogDescription>
          </DialogHeader>
          {url && (
            <div className="grid gap-3 pt-2">
              <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={copy}>Copy</Button>
                <a href={url} target="_blank" rel="noreferrer"><Button>Open</Button></a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
